import React, { useState } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useRsvp } from '@/src/modules/rsvp';

interface RSVPNeonSectionProps {
  invitationId: string | null;
  isLive: boolean;
  language: 'en' | 'mr';
}

export const RSVPNeonSection: React.FC<RSVPNeonSectionProps> = ({ invitationId, isLive, language }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [attendingStatus, setAttendingStatus] = useState<'yes' | 'no' | null>(null);
  const [guestCount, setGuestCount] = useState(1);
  const [message, setMessage] = useState('');

  const { state, errorMessage, submit } = useRsvp(invitationId, isLive);
  const isSubmitting = state === 'loading';

  const handleSubmitRSVP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !attendingStatus || isSubmitting) return;

    await submit({
      guestName: name.trim(),
      guestEmail: email.trim() || undefined,
      status: attendingStatus === 'yes' ? 'ACCEPTED' : 'DECLINED',
      headcount: attendingStatus === 'yes' ? guestCount : 1,
      message: message.trim() || undefined,
    });
  };

  if (state === 'success') {
    return (
      <section className="relative py-24 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-block mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-pink-500 rounded-full flex items-center justify-center animate-pulse">
              <CheckCircle2 size={40} className="text-white" />
            </div>
          </div>
          <h2 className="text-4xl font-black text-white mb-4">
            {language === 'mr' ? 'धन्यवाद!' : 'Thank You!'}
          </h2>
          <p className="text-gray-300 text-lg">
            {language === 'mr'
              ? 'आपली RSVP स्वीकारली गेली आहे। आपल्या आगमनचा प्रतीक्षा करत आहे!'
              : 'Your RSVP has been received. See you at the party!'}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-24 px-4 bg-gradient-to-b from-transparent via-slate-900/20 to-slate-900/50">
      <div className="max-w-2xl mx-auto">
        <h2 className="text-center text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-pink-400 mb-12">
          {language === 'mr' ? '🎊 RSVP करा' : '🎊 RSVP Now'}
        </h2>

        <form onSubmit={handleSubmitRSVP} className="space-y-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-3">
              {language === 'mr' ? 'आपले नाव' : 'Your Name'} *
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={language === 'mr' ? 'नाव' : 'Full name'}
              className="w-full bg-slate-800/50 border border-slate-700 text-white rounded-lg px-5 py-3 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-3">
              {language === 'mr' ? 'ईमेल' : 'Email'}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              className="w-full bg-slate-800/50 border border-slate-700 text-white rounded-lg px-5 py-3 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all"
            />
          </div>

          {/* Attendance Status */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-4">
              {language === 'mr' ? 'आप आहार' : 'Will You Join Us?'} *
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setAttendingStatus('yes')}
                className={`py-4 px-6 rounded-lg font-bold transition-all transform hover:scale-105 flex items-center justify-center gap-2 ${
                  attendingStatus === 'yes'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/50'
                    : 'bg-slate-800 border border-slate-700 text-gray-300 hover:border-cyan-400'
                }`}
              >
                <CheckCircle2 size={20} />
                {language === 'mr' ? 'होय' : 'Yes!'}
              </button>
              <button
                type="button"
                onClick={() => setAttendingStatus('no')}
                className={`py-4 px-6 rounded-lg font-bold transition-all transform hover:scale-105 flex items-center justify-center gap-2 ${
                  attendingStatus === 'no'
                    ? 'bg-gradient-to-r from-pink-500 to-red-600 text-white shadow-lg shadow-pink-500/50'
                    : 'bg-slate-800 border border-slate-700 text-gray-300 hover:border-pink-400'
                }`}
              >
                <XCircle size={20} />
                {language === 'mr' ? 'नाही' : 'No'}
              </button>
            </div>
          </div>

          {/* Guest Count (only if attending) */}
          {attendingStatus === 'yes' && (
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-3">
                {language === 'mr' ? 'अतिथी संख्या' : 'Number of Guests'}
              </label>
              <select
                value={guestCount}
                onChange={(e) => setGuestCount(parseInt(e.target.value))}
                className="w-full bg-slate-800/50 border border-slate-700 text-white rounded-lg px-5 py-3 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all"
              >
                {[1, 2, 3, 4, 5].map((num) => (
                  <option key={num} value={num}>
                    {num} {num === 1 ? (language === 'mr' ? 'अतिथी' : 'guest') : language === 'mr' ? 'अतिथी' : 'guests'}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Message */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-3">
              {language === 'mr' ? 'संदेश' : 'Message'}
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={language === 'mr' ? 'कोई संदेश...' : 'Any message or dietary requirements?'}
              rows={3}
              className="w-full bg-slate-800/50 border border-slate-700 text-white rounded-lg px-5 py-3 focus:border-pink-400 focus:ring-2 focus:ring-pink-400/20 transition-all resize-none"
            />
          </div>

          {/* Submission error */}
          {state === 'error' && errorMessage && (
            <p className="text-red-400 text-sm text-center font-semibold">{errorMessage}</p>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || !attendingStatus}
            className="w-full bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white font-bold py-4 rounded-lg hover:shadow-2xl hover:shadow-cyan-500/50 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (language === 'mr' ? 'सबमिट कर रहे हैं...' : 'Submitting...') : (language === 'mr' ? 'RSVP सबमिट करें' : 'Submit RSVP')}
          </button>
        </form>
      </div>
    </section>
  );
};
